(() => {
  const root = document.querySelector('[data-ozana-cookie-consent]');
  if (!root || root.dataset.initialized === 'true') return;
  root.dataset.initialized = 'true';

  const summary = root.querySelector('[data-ozana-cookie-summary]');
  const preferencesPanel = root.querySelector('[data-ozana-cookie-preferences]');
  const status = root.querySelector('[data-ozana-cookie-status]');
  const switches = {
    preferences: root.querySelector('input[name="preferences"]'),
    analytics: root.querySelector('input[name="analytics"]'),
    marketing: root.querySelector('input[name="marketing"]'),
  };

  let privacyApi = null;
  let hasSavedChoice = false;
  let lastOpener = null;
  let saving = false;

  const getConsent = () => {
    if (!privacyApi || typeof privacyApi.currentVisitorConsent !== 'function') return {};
    try {
      return privacyApi.currentVisitorConsent() || {};
    } catch (_error) {
      return {};
    }
  };

  const syncSwitches = () => {
    const consent = getConsent();
    Object.keys(switches).forEach((key) => {
      if (switches[key]) switches[key].checked = consent[key] === 'yes';
    });
    hasSavedChoice = Object.keys(switches).every((key) => consent[key] === 'yes' || consent[key] === 'no');
    return consent;
  };

  const setView = (view) => {
    const showPreferences = view === 'preferences';
    summary.hidden = showPreferences;
    preferencesPanel.hidden = !showPreferences;
    root.dataset.view = view;
    status.textContent = '';
  };

  const show = (view = 'summary', opener = null) => {
    lastOpener = opener || document.activeElement;
    syncSwitches();
    setView(view);
    root.hidden = false;
    window.requestAnimationFrame(() => {
      const focusTarget = view === 'preferences'
        ? root.querySelector('.ozana-cookie__close')
        : root.querySelector('[data-ozana-cookie-action="accept"]');
      if (opener && focusTarget) focusTarget.focus({ preventScroll: true });
    });
  };

  const hide = () => {
    root.hidden = true;
    setView('summary');
    if (lastOpener && typeof lastOpener.focus === 'function') lastOpener.focus({ preventScroll: true });
    lastOpener = null;
  };

  const closePreferences = () => {
    if (hasSavedChoice) {
      hide();
      return;
    }

    setView('summary');
    root.querySelector('[data-ozana-cookie-action="customize"]')?.focus({ preventScroll: true });
  };

  const setBusy = (isBusy) => {
    saving = isBusy;
    root.querySelectorAll('button').forEach((button) => { button.disabled = isBusy; });
    if (isBusy) status.textContent = root.dataset.savingLabel || '';
  };

  const waitForShopifyPrivacy = () => new Promise((resolve, reject) => {
    let attempts = 0;
    const check = () => {
      if (window.Shopify?.customerPrivacy || typeof window.Shopify?.loadFeatures === 'function') {
        resolve(window.Shopify);
        return;
      }
      attempts += 1;
      if (attempts >= 30) {
        reject(new Error('API de confidentialité Shopify indisponible.'));
        return;
      }
      window.setTimeout(check, 100);
    };
    check();
  });

  const loadPrivacyApi = async () => {
    const shopify = await waitForShopifyPrivacy();
    if (shopify.customerPrivacy) {
      privacyApi = shopify.customerPrivacy;
      return privacyApi;
    }

    return new Promise((resolve, reject) => {
      let settled = false;
      const finish = (error, api) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        if (error) {
          reject(error);
          return;
        }
        resolve(api);
      };
      const timer = window.setTimeout(() => {
        finish(new Error('Le chargement de l\'API de confidentialité a expiré.'));
      }, 4000);

      shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.2' }],
        (error) => {
          if (error || !window.Shopify?.customerPrivacy) {
            finish(error || new Error('API de confidentialité Shopify indisponible.'));
            return;
          }
          privacyApi = window.Shopify.customerPrivacy;
          finish(null, privacyApi);
        },
      );
    });
  };

  const saveConsent = async (consent) => {
    if (saving) return;
    setBusy(true);

    try {
      if (!privacyApi) await loadPrivacyApi();
      await new Promise((resolve, reject) => {
        let completed = false;
        const timer = window.setTimeout(() => {
          if (!completed) reject(new Error('Le délai d’enregistrement est dépassé.'));
        }, 3500);

        privacyApi.setTrackingConsent(consent, (result) => {
          completed = true;
          window.clearTimeout(timer);
          const resultError = result?.error || (result?.type === 'error' ? result.message || result : null);
          if (resultError) {
            reject(new Error(Array.isArray(resultError) ? resultError.join(' ') : String(resultError)));
            return;
          }
          resolve(result);
        });
      });

      syncSwitches();
      hasSavedChoice = true;
      document.dispatchEvent(new CustomEvent('ozana:consent-updated', { detail: consent }));
      hide();
    } catch (_error) {
      status.textContent = root.dataset.saveErrorLabel || '';
    } finally {
      setBusy(false);
    }
  };

  root.addEventListener('click', (event) => {
    const control = event.target.closest('[data-ozana-cookie-action]');
    if (!control) return;

    event.preventDefault();
    event.stopPropagation();

    const action = control.dataset.ozanaCookieAction;
    if (action === 'accept') {
      saveConsent({ preferences: true, analytics: true, marketing: true });
    } else if (action === 'reject') {
      saveConsent({ preferences: false, analytics: false, marketing: false });
    } else if (action === 'customize') {
      syncSwitches();
      setView('preferences');
      root.querySelector('.ozana-cookie__close')?.focus({ preventScroll: true });
    } else if (action === 'close') {
      closePreferences();
    }
  });

  preferencesPanel.addEventListener('submit', (event) => {
    event.preventDefault();
    saveConsent({
      preferences: Boolean(switches.preferences?.checked),
      analytics: Boolean(switches.analytics?.checked),
      marketing: Boolean(switches.marketing?.checked),
    });
  });

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-ozana-cookie-open]');
    if (!opener) return;
    event.preventDefault();
    show('preferences', opener);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || root.hidden || saving) return;
    if (root.dataset.view === 'preferences') {
      closePreferences();
    } else if (hasSavedChoice) {
      hide();
    }
  });

  loadPrivacyApi()
    .then(() => {
      const consent = syncSwitches();
      const undecided = Object.keys(switches).some((key) => consent[key] !== 'yes' && consent[key] !== 'no');
      if (undecided || (window.Shopify && window.Shopify.designMode)) show('summary');
    })
    .catch(() => {
      show('summary');
      status.textContent = root.dataset.loadErrorLabel || '';
    });
})();
